import React from 'react';
import {
    Box,
    Container,
    Typography,
    Link,
    IconButton,
    useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';


// Import your logo
import {logoTransparent01 } from '../../assets/images';

const SERVICE_AREAS_MAP_SRC =
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d110502.76983735489!2d-97.80961841658829!3d30.307840990176025!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8644b599a0cc032f%3A0x5d9b464bd469d57a!2sAustin%2C%20TX!5e0!3m2!1sen!2sus!4v1684786047813!5m2!1sen!2sus';

const Footer = () => {
    const theme = useTheme();
    const location = useLocation();
    const currentYear = new Date().getFullYear();

    const socialIcons = [
        { Icon: FacebookIcon, label: 'Facebook' },
        { Icon: TwitterIcon, label: 'Twitter' },
        { Icon: InstagramIcon, label: 'Instagram' },
        { Icon: LinkedInIcon, label: 'LinkedIn' },
    ];

    const quickLinks = [
        { text: 'Home', path: '/' },
        { text: 'About Us', path: '/about' },
        { text: 'Our Services', path: '/services' },
        { text: 'Careers', path: '/careers' },
        { text: 'Contact Us', path: '/contact' },
        { text: 'Privacy Policy', path: '/privacy' },
    ];

    return (
        <Box
            component="footer"
            sx={{
                bgcolor: '#0182ad',
                color: '#fff',
                py: { xs: 1, sm: 1, md: 2 },
                mt: 'auto'
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flexBasis: { xs: '100%', sm: '22%' }, mb: { xs: 4, sm: 0 } }}>
                        <Box>
                            <img src={logoTransparent01} alt="Pivotal Behavioral Services Logo" style={{ maxWidth: '300px', height: 'auto' }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {socialIcons.map(({ Icon, label }, index) => (
                                <IconButton
                                    key={index}
                                    aria-label={label}
                                    sx={{
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)', transform: 'scale(1.1)' },
                                        transition: 'all 0.3s'
                                    }}
                                >
                                    <Icon />
                                </IconButton>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ flexBasis: { xs: '100%', sm: '22%' }, mb: { xs: 4, sm: 0 } }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '2px solid #fff', pb: 1, mb: 2 }}>
                            Quick Links
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {quickLinks.map(({ text, path }, index) => (
                                <Link
                                    key={index}
                                    component={RouterLink}
                                    to={path}
                                    color="inherit"
                                    sx={{
                                        textDecoration: 'none',
                                        fontSize: '0.9rem',
                                        '&:hover': {
                                            color: theme.palette.secondary.main,
                                        },
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {text}
                                    <ChevronRightIcon sx={{ fontSize: '1rem', ml: 1 }} />
                                </Link>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ flexBasis: { xs: '100%', sm: '22%' }, mb: { xs: 4, sm: 0 } }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '2px solid #fff', pb: 1, mb: 2 }}>
                            Contact Us
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PhoneIcon sx={{ mr: 1, fontSize: '0.9rem' }} />
                            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>(202) 492-6575</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <EmailIcon sx={{ mr: 1, fontSize: '0.9rem' }} />
                            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>info@pivotalbs.com</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flexBasis: { xs: '100%', sm: '22%' } }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', borderBottom: '2px solid #fff', pb: 1, mb: 2 }}>
                            Service Areas
                        </Typography>
                        <Box
                            key={location.pathname}
                            component="iframe"
                            title="Pivotal Behavioral Services service areas map"
                            src={SERVICE_AREAS_MAP_SRC}
                            width="100%"
                            height="200"
                            style={{ border: 0, borderRadius: '8px', backgroundColor: '#e8e8e8' }}
                            allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', color: '#fff', fontSize: '0.8rem' }}>
                            Serving Austin, Houston, and Dallas, Texas
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', mt: 4, pt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 1 }}>
                        © {currentYear} Pivotal Behavioral Services LLC. All rights reserved.
                    </Typography>
                    <Link
                        component={RouterLink}
                        to="/privacy"
                        color="inherit"
                        sx={{
                            fontSize: '0.8rem',
                            textDecoration: 'underline',
                            '&:hover': { color: theme.palette.secondary.main },
                        }}
                    >
                        Privacy Policy
                    </Link>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;
